import React, { useEffect, useState } from "react"
import { Link as RouterLink, useLocation, useParams } from "react-router-dom"
import { Helmet } from "react-helmet";
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Link as MuiLink,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { CreateLeiService } from "../../api/services/create-lei"
import { SearchLeiCode } from "../../api/services/search-lei"
import Button from "../../components/ui/ui kit/Button"
import Container from "../../components/ui/ui kit/Container"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import EmailIcon from "@mui/icons-material/Email"
import AdSlot from "../../components/functional/Ads/AdSlot";
import CheckLeiNumber from "../../components/functional/CheckLeiNumber/CheckLeiNumber";
import { Faq } from "../../components/Faq/Faq";
import { AxiosError } from 'axios';

const formatDateTime = (date?: string | null) => (date ? date.slice(0, 19).replace("T", " ") : undefined);
const formatDateOnly = (date?: string | null) => (date ? date.slice(0, 10) : undefined);
const humanize = (value?: string | null) => (value ? value.replace(/_/g, " ") : undefined);

const leiStatusColor = (status?: string): "success" | "warning" | "error" | "default" => {
    if (status === "ISSUED") return "success";
    if (status?.startsWith("PENDING")) return "warning";
    if (status === "LAPSED" || status === "ANNULLED" || status === "DUPLICATE") return "error";
    return "default";
};

const entityStatusColor = (status?: string): "success" | "default" => (status === "ACTIVE" ? "success" : "default");

const InfoRow = ({ label, value, sm = 6 }: { label: string; value?: React.ReactNode; sm?: number }) => (
    <Grid item xs={12} sm={sm}>
        <Typography variant="caption" color="text.secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body1" component="div">
            {value === undefined || value === null || value === "" ? "N/A" : value}
        </Typography>
    </Grid>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2 }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

interface NamePart {
    name: string;
    language?: string;
    type?: string;
}

interface AddressPart {
    addressLines?: string[];
    city?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    type?: string;
}

export const Lei = () => {
    const params = useParams()
    const location = useLocation();
    const pageUrl = window.location.origin + location.pathname;
    const [leiData, setLeiData] = useState<any>()
    const [countries, setCountries] = useState<any>({})
    const [LouName, setLouName] = useState<string>("");
    const [LeiIssuerName, setLeiIssuerName] = useState<string>("");
    const [LeiIssuerCode, setLeiIssuerCode] = useState<string>("");
    const [DirectParentName, setDirectParentName] = useState<string>("");
    const [DirectParentCode, setDirectParentCode] = useState<string>("");
    const [directParentException, setDirectParentException] = useState<{ category?: string; reason?: string } | null>(null);
    const [UltimateParentName, setUltimateParentName] = useState<string>("");
    const [UltimateParentCode, setUltimateParentCode] = useState<string>("");
    const [ultimateParentException, setUltimateParentException] = useState<{ category?: string; reason?: string } | null>(null);
    const [directChildren, setDirectChildren] = useState<{ legalName: string, lei: string }[]>([]);
    const [directChildrenTotal, setDirectChildrenTotal] = useState<number>(0);
    const [ultimateChildren, setUltimateChildren] = useState<{ legalName: string, lei: string }[]>([]);
    const [ultimateChildrenTotal, setUltimateChildrenTotal] = useState<number>(0);
    const [legalFormTransliteratedName, setLegalFormTransliteratedName] = useState<string | null>(null);
    const [raInternationalOrganizationName, setRaInternationalOrganizationName] = useState<string | null>(null);
    const [raCountry, setRaCountry] = useState<string | null>(null);
    const [raWebsite, setRaWebsite] = useState<string | null>(null);
    const [vaInternationalOrganizationName, setVaInternationalOrganizationName] = useState<string | null>(null);
    const [vaCountry, setVaCountry] = useState<string | null>(null);
    const [vaWebsite, setVaWebsite] = useState<string | null>(null);
    const [isinsTotal, setIsinsTotal] = useState<number>(0);

    const lei = params.lei

    const [leiExists, setLeiExists] = useState<boolean | null>(null);
    const handleStatusChecked = (exists: boolean) => {
        setLeiExists(exists);
    };

    const requestLei = async () => {
        if (lei) {
            const res = await SearchLeiCode.getLei(lei)
            setLeiData(res.data)
        }
    }

    const requestCountries = async () => {
        try {
            const countries = await CreateLeiService.requestAllCountries()
            setCountries(countries.data.data)
        } catch (error) {
            console.log(error)
        }
    }

    function getFlag(countryCode: string): string {
        const OFFSET = 127397;
        return countryCode
            .toUpperCase()
            .replace(/./g,
                (char: string) => String.fromCodePoint(char.charCodeAt(0) + OFFSET)
            );
    }

    function getValueOrNA(value: any) {
        if (Array.isArray(value)) {
            if (value.length > 0) {
                return value.map((val) => val.name).join(", ");
            }
            return "N/A";
        } else {
            return value || "N/A";
        }
    }

    const countryLabel = (code?: string) => (code ? `${code} - ${countries[code] || code}` : undefined);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        requestLei()
        requestCountries()
    }, [lei])

    const ctx = leiData

    useEffect(() => {
        const getManagingLou = async (lei: string) => {
            try {
                const response = await SearchLeiCode.getManagingLou(lei);
                if (response.data) {
                    setLouName(response.data.data.attributes.entity.legalName.name);
                } else {
                    setLouName("No data");
                }
            } catch (error) {
                console.error("Error fetching managing Lou:", error);
            }
        };

        if (ctx) {
            getManagingLou(ctx.data.attributes.lei);
        }
    }, [ctx]);


    useEffect(() => {
        const getLeiIssuer = async (lei: string) => {
            try {
                const response = await SearchLeiCode.getLeiIssuer(lei);
                if (response.data) {
                    setLeiIssuerName(response.data.data.attributes.name);
                    setLeiIssuerCode(response.data.data.attributes.lei);
                } else {
                    setLeiIssuerName("No data");
                    setLeiIssuerCode("No data");
                }
            } catch (error) {
                console.error("Error fetching LEI issuer:", error);
            }
        };

        if (ctx) {
            getLeiIssuer(ctx.data.attributes.lei);
        }
    }, [ctx]);


    // Getting Direct Parent info - falls back to the reporting exception
    // (e.g. NO_KNOWN_PERSON, NON_CONSOLIDATING) GLEIF records when an
    // entity has no parent, instead of leaving the field blank.
    useEffect(() => {
        const getDirectParent = async (lei: string) => {
            try {
                const response = await SearchLeiCode.getDirectParent(lei);
                if (response.data) {
                    setDirectParentName(response.data.data.attributes.entity.legalName.name);
                    setDirectParentCode(response.data.data.attributes.lei);
                }
            } catch (error) {
                const axiosError = error as AxiosError;
                if (axiosError.response && axiosError.response.status === 404) {
                    try {
                        const exceptionRes = await SearchLeiCode.getDirectParentReportingException(lei);
                        setDirectParentException(exceptionRes.data.data.attributes);
                    } catch (exceptionError) {
                        console.log("No direct parent or reporting exception found.");
                    }
                } else {
                    console.error("Error fetching direct parent:", axiosError);
                }
            }
        };

        if (ctx) {
            getDirectParent(ctx.data.attributes.lei);
        }
    }, [ctx]);

    // Getting Ultimate Parent Info (same reporting-exception fallback)
    useEffect(() => {
        const getUltimateParent = async (lei: string) => {
            try {
                const response = await SearchLeiCode.getUltimateParent(lei);
                if (response.data) {
                    setUltimateParentName(response.data.data.attributes.entity.legalName.name);
                    setUltimateParentCode(response.data.data.attributes.lei);
                }
            } catch (error) {
                const axiosError = error as AxiosError;
                if (axiosError.response && axiosError.response.status === 404) {
                    try {
                        const exceptionRes = await SearchLeiCode.getUltimateParentReportingException(lei);
                        setUltimateParentException(exceptionRes.data.data.attributes);
                    } catch (exceptionError) {
                        console.log("No ultimate parent or reporting exception found.");
                    }
                } else {
                    console.error("Error fetching ultimate parent:", axiosError);
                }
            }
        };

        if (ctx) {
            getUltimateParent(ctx.data.attributes.lei);
        }
    }, [ctx]);

    interface ChildEntity {
        type: string;
        id: string;
        attributes: {
            lei: string;
            entity: {
                legalName: {
                    name: string;
                    language: string;
                };
            };
        };
    }

    // Getting info for direct + ultimate children
    useEffect(() => {
        const fetchChildren = async () => {
            if (ctx && ctx.data.attributes.lei) {
                try {
                    const response = await SearchLeiCode.getDirectChildren(ctx.data.attributes.lei);
                    const childrenData: ChildEntity[] = response.data.data;
                    setDirectChildren(childrenData.map((child) => ({
                        legalName: child.attributes.entity.legalName.name,
                        lei: child.attributes.lei
                    })));
                    setDirectChildrenTotal(response.data.meta?.pagination?.total ?? childrenData.length);
                } catch (error) {
                    console.error("Error fetching Direct Children:", error);
                }
                try {
                    const response = await SearchLeiCode.getUltimateChildren(ctx.data.attributes.lei);
                    const childrenData: ChildEntity[] = response.data.data;
                    setUltimateChildren(childrenData.map((child) => ({
                        legalName: child.attributes.entity.legalName.name,
                        lei: child.attributes.lei
                    })));
                    setUltimateChildrenTotal(response.data.meta?.pagination?.total ?? childrenData.length);
                } catch (error) {
                    console.error("Error fetching Ultimate Children:", error);
                }
            }
        };
        fetchChildren();
    }, [ctx]);

    // Getting info for legal form
    useEffect(() => {
        const fetchLegalForm = async () => {
            if (ctx?.data?.attributes?.entity?.legalForm?.id) {
                try {
                    const response = await SearchLeiCode.getLegalForm(ctx.data.attributes.entity.legalForm.id);
                    setLegalFormTransliteratedName(response.data.data.attributes.names[0].transliteratedName);
                } catch (error) {
                    console.error("Error fetching legal form:", error);
                }
            }
        };
        fetchLegalForm();
    }, [ctx]);

    // Getting info for reg authority (both the registering RA and the RA
    // that validated the LEI data, which can differ)
    useEffect(() => {
        const fetchRegistrationAuthority = async () => {
            if (ctx && ctx.data.attributes.entity.registeredAt.id) {
                try {
                    const response = await SearchLeiCode.getRegistrationAuthority(ctx.data.attributes.entity.registeredAt.id);
                    setRaInternationalOrganizationName(response.data.data.attributes.internationalOrganizationName);
                    setRaCountry(response.data.data.attributes.jurisdictions[0]?.country);
                    setRaWebsite(response.data.data.attributes.website);
                } catch (error) {
                    console.error("Error fetching Registration Authority:", error);
                }
            }
            if (ctx && ctx.data.attributes.registration.validatedAt.id) {
                try {
                    const response = await SearchLeiCode.getRegistrationAuthority(ctx.data.attributes.registration.validatedAt.id);
                    setVaInternationalOrganizationName(response.data.data.attributes.internationalOrganizationName);
                    setVaCountry(response.data.data.attributes.jurisdictions[0]?.country);
                    setVaWebsite(response.data.data.attributes.website);
                } catch (error) {
                    console.error("Error fetching Registration Authority:", error);
                }
            }
        };

        fetchRegistrationAuthority();
    }, [ctx]);

    // ISIN count - the isins relationship can carry hundreds of pages, so
    // only the total count is fetched, not every ISIN.
    useEffect(() => {
        const fetchIsinsCount = async () => {
            if (ctx?.data?.relationships?.isins?.links?.related && ctx?.data?.attributes?.lei) {
                try {
                    const response = await SearchLeiCode.getIsinsCount(ctx.data.attributes.lei);
                    setIsinsTotal(response.data.meta?.pagination?.total ?? 0);
                } catch (error) {
                    console.error("Error fetching ISIN count:", error);
                }
            }
        };
        fetchIsinsCount();
    }, [ctx]);


    if (!ctx) {
        return (
            <Container>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    const leiNumber = ctx.data.attributes.lei
    const leiStatus = ctx.data.attributes.registration.status
    const leiNextRenewalDate = formatDateTime(ctx.data.attributes.registration.nextRenewalDate)
    const leiManagingLOU = ctx.data.attributes.registration.managingLou
    const leiCorroborationLevel = ctx.data.attributes.registration.corroborationLevel
    const entityLegalName = getValueOrNA(ctx.data.attributes.entity.legalName.name)
    const entityStatus = ctx.data.attributes.entity.status
    const entityJurisdiction = getValueOrNA(ctx.data.attributes.entity.jurisdiction)
    const entityCategory = ctx.data.attributes.entity.category
    const entitySubCategory = ctx.data.attributes.entity.subCategory
    const otherNames: NamePart[] = ctx.data.attributes.entity.otherNames ?? []
    const transliteratedOtherNames: NamePart[] = ctx.data.attributes.entity.transliteratedOtherNames ?? []
    const entityRegisteredAtId = getValueOrNA(ctx.data.attributes.entity.registeredAt.id)
    const entityRegisteredAs = getValueOrNA(ctx.data.attributes.entity.registeredAs)
    const entityValidatedAtId = ctx.data.attributes.registration.validatedAt.id
    const entityValidatedAs = ctx.data.attributes.registration.validatedAs
    const otherValidationAuthorities: { validatedAt?: { id?: string }, validatedAs?: string }[] = ctx.data.attributes.registration.otherValidationAuthorities ?? []
    const entityLegalAddressCity = ctx.data.attributes.entity.legalAddress.city
    const entityLegalAddressCountry = ctx.data.attributes.entity.legalAddress.country
    const entityLegalAddressRegion = ctx.data.attributes.entity.legalAddress.region
    const entityLegalAddressLines = ctx.data.attributes.entity.legalAddress.addressLines.join(", ")
    const entityLegalAddressPostalCode = ctx.data.attributes.entity.legalAddress.postalCode
    const entityHeadquartersAddressCity = ctx.data.attributes.entity.headquartersAddress.city
    const entityHeadquartersAddressCountry = ctx.data.attributes.entity.headquartersAddress.country
    const entityHeadquartersAddressRegion = ctx.data.attributes.entity.headquartersAddress.region
    const entityHeadquartersAddressLines = ctx.data.attributes.entity.headquartersAddress.addressLines.join(", ")
    const entityHeadquartersAddressPostalCode = ctx.data.attributes.entity.headquartersAddress.postalCode
    const otherAddresses: AddressPart[] = ctx.data.attributes.entity.otherAddresses ?? []
    const entityFlag = getFlag(ctx.data.attributes.entity.legalAddress.country)
    const entityLegalFormId = getValueOrNA(ctx.data.attributes.entity.legalForm.id)
    const entityCreationDate = formatDateOnly(ctx.data.attributes.entity.creationDate) ?? "N/A"
    const entityCreationDateFull = formatDateTime(ctx.data.attributes.entity.creationDate) ?? "N/A"
    const entityLastUpdate = formatDateOnly(ctx.data.attributes.registration.lastUpdateDate) ?? "N/A"
    const entityLastUpdateFull = formatDateTime(ctx.data.attributes.registration.lastUpdateDate) ?? "N/A"
    const initialRegistrationDate = formatDateTime(ctx.data.attributes.registration.initialRegistrationDate)
    const associatedEntityLei = ctx.data.attributes.entity.associatedEntity?.lei
    const associatedEntityName = ctx.data.attributes.entity.associatedEntity?.name
    const expirationDate = ctx.data.attributes.entity.expiration?.date
    const expirationReason = ctx.data.attributes.entity.expiration?.reason
    const successorEntities: { lei?: string, name?: string }[] = ctx.data.attributes.entity.successorEntities ?? []
    const mic = ctx.data.attributes.mic
    const ocid = ctx.data.attributes.ocid
    const qcc = ctx.data.attributes.qcc
    const gem = ctx.data.attributes.gem
    const spglobal: string[] = ctx.data.attributes.spglobal ?? []
    const conformityFlag = ctx.data.attributes.conformityFlag
    const isinsLink = ctx.data.relationships?.isins?.links?.related
    const fieldModificationsLink = ctx.data.relationships?.["field-modifications"]?.links?.related

    const recentEvents = (ctx.data.attributes.entity.eventGroups ?? [])
        .flatMap((group: any) => group.events ?? [])
        .sort((a: any, b: any) => (a.recordedDate < b.recordedDate ? 1 : -1))
        .slice(0, 10);


    const schema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": pageUrl
        },
        "headline": `${entityLegalName} from ${entityLegalAddressCity}, ${entityLegalAddressCountry} ${entityFlag} LEI ${leiNumber}`,
        "image": "https://app.leipapa.com/logo512.png",
        "author": {
            "@type": "Organization",
            "name": "LEIpapa",
            "url": "https://leipapa.com"
        },
        "publisher": {
            "@type": "Organization",
            "name": "LEIpapa",
            "logo": {
                "@type": "ImageObject",
                "url": "https://app.leipapa.com/logo512.png"
            }
        },
        "datePublished": entityCreationDate,
        "dateModified": entityLastUpdate
    };

    return (
        <Container>
            <Helmet>
                <title>{entityLegalName} from {entityLegalAddressCity}, {entityLegalAddressCountry} {entityFlag} LEI {leiNumber}</title>
                <meta
                    name="description"
                    content={`${entityLegalName} registered at ${entityLegalAddressCity}, ${entityLegalAddressCountry} ${entityFlag} on ${entityCreationDate}. LEI ${leiNumber}, LEI ${leiStatus}, Entity ${entityStatus}, Company Information`}
                />
                <meta name="keywords"
                    content={`LEI information, Company Information, ${entityLegalName}, LEI ${leiNumber}, Jurisdiction ${entityLegalAddressCountry}, ${entityLegalAddressCity} city, official company page, official company data`} />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:title" content={`${entityLegalName} from ${entityLegalAddressCity}, ${entityLegalAddressCountry} ${entityFlag} LEI ${leiNumber}`} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://app.leipapa.com/leipapa.png" />
                <meta property="og:url" content={pageUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@leipapa_com" />
                <meta name="twitter:title" content={`${entityLegalName} from ${entityLegalAddressCity}, ${entityLegalAddressCountry} ${entityFlag} LEI ${leiNumber}`} />
                <meta name="twitter:description" content={`${entityLegalName} registered at ${entityLegalAddressCity}, ${entityLegalAddressCountry} ${entityFlag} on ${entityCreationDate}. LEI ${leiNumber}, LEI status ${leiStatus}, Entity Status ${entityStatus}`} />
                <meta name="twitter:image" content="https://app.leipapa.com/leipapa.png" />
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>

            <Box sx={{ py: { xs: 3, sm: 4 } }}>
                <Typography variant="h4" component="h1" fontWeight={700} sx={{ color: "primary.dark" }}>
                    {entityLegalName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Actual information about the company
                </Typography>

                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <InfoRow label="LEI Code" value={<Typography fontWeight={600}>{leiNumber}</Typography>} />
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block">LEI Status</Typography>
                            <Chip size="small" label={humanize(leiStatus)} color={leiStatusColor(leiStatus)} sx={{ mt: 0.5 }} />
                        </Grid>
                        <InfoRow label="Country" value={`${entityFlag} ${countryLabel(entityLegalAddressCountry)}, ${entityLegalAddressCity}`} />
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block">Entity Status</Typography>
                            <Chip size="small" label={humanize(entityStatus)} color={entityStatusColor(entityStatus)} sx={{ mt: 0.5 }} />
                        </Grid>
                    </Grid>
                </Paper>

                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <CheckLeiNumber leiNumber={leiNumber} entityLegalName={entityLegalName} onStatusChecked={handleStatusChecked} />
                </Paper>

                <Section title="Main Information">
                    <Grid container spacing={2}>
                        <InfoRow label="Legal Name (Primary)" value={entityLegalName} sm={12} />
                        {otherNames.length > 0 && (
                            <InfoRow
                                label="Other Names"
                                sm={12}
                                value={
                                    <Stack spacing={0.5}>
                                        {otherNames.map((name, index) => (
                                            <Box key={index}>
                                                {name.name}
                                                {name.type && <Chip size="small" label={humanize(name.type)} sx={{ ml: 1 }} />}
                                            </Box>
                                        ))}
                                    </Stack>
                                }
                            />
                        )}
                        {transliteratedOtherNames.length > 0 && (
                            <InfoRow label="Transliterated Names" sm={12} value={transliteratedOtherNames.map((n) => n.name).join(", ")} />
                        )}
                        <InfoRow
                            label="Registered At"
                            value={entityRegisteredAtId !== "N/A" ? (
                                <Stack spacing={0.25}>
                                    <span>
                                        {raInternationalOrganizationName}
                                        {raWebsite && <> &middot; <MuiLink href={raWebsite} target="_blank" rel="noopener noreferrer">website</MuiLink></>}
                                    </span>
                                    <span>{raCountry}</span>
                                    <span>{entityRegisteredAtId}</span>
                                </Stack>
                            ) : undefined}
                        />
                        <InfoRow label="Registered As" value={entityRegisteredAs} />
                        <InfoRow label="Jurisdiction Of Formation" value={entityJurisdiction} />
                        <InfoRow label="Entity Category" value={humanize(entityCategory)} />
                        {entitySubCategory && <InfoRow label="Entity Sub-Category" value={humanize(entitySubCategory)} />}
                        <InfoRow
                            label="Entity Legal Form"
                            value={legalFormTransliteratedName || entityLegalFormId !== "N/A" ? (
                                <>{legalFormTransliteratedName}{entityLegalFormId !== "N/A" && ` (${entityLegalFormId})`}</>
                            ) : undefined}
                        />
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block">Entity Status</Typography>
                            <Chip size="small" label={humanize(entityStatus)} color={entityStatusColor(entityStatus)} sx={{ mt: 0.5 }} />
                        </Grid>
                        {associatedEntityLei && (
                            <InfoRow
                                label="Associated Entity (Branch Of)"
                                value={<MuiLink component={RouterLink} to={`/lei-details/${associatedEntityLei}`} target="_blank" rel="noopener noreferrer">{associatedEntityName || associatedEntityLei}</MuiLink>}
                            />
                        )}
                        {expirationDate && (
                            <InfoRow label="Expiration" value={`${formatDateOnly(expirationDate)}${expirationReason ? " - " + humanize(expirationReason) : ""}`} />
                        )}
                        {successorEntities.length > 0 && (
                            <InfoRow
                                label="Successor Entities"
                                sm={12}
                                value={
                                    <Stack spacing={0.5}>
                                        {successorEntities.map((successor, index) => (
                                            <Box key={index}>
                                                {successor.lei ? (
                                                    <MuiLink component={RouterLink} to={`/lei-details/${successor.lei}`} target="_blank" rel="noopener noreferrer">
                                                        {successor.name || successor.lei}
                                                    </MuiLink>
                                                ) : (successor.name || "N/A")}
                                            </Box>
                                        ))}
                                    </Stack>
                                }
                            />
                        )}
                        {conformityFlag && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Conformity Flag</Typography>
                                <Chip size="small" label={humanize(conformityFlag)} color={conformityFlag === "CONFORMING" ? "success" : "warning"} sx={{ mt: 0.5 }} />
                            </Grid>
                        )}
                    </Grid>
                </Section>

                <Section title="Identifiers">
                    <Grid container spacing={2}>
                        <InfoRow label="BIC" value={Array.isArray(ctx.data.attributes.bic) ? ctx.data.attributes.bic.join(", ") : undefined} />
                        <InfoRow label="MIC" value={mic} />
                        <InfoRow label="OCID" value={ocid} />
                        <InfoRow label="QCC" value={qcc} />
                        <InfoRow label="GEM" value={gem} />
                        <InfoRow label="S&P Global IDs" value={spglobal.length > 0 ? spglobal.join(", ") : undefined} />
                        <InfoRow
                            label="ISIN Codes"
                            value={isinsTotal > 0 && isinsLink ? (
                                <MuiLink href={isinsLink} target="_blank" rel="noopener noreferrer">
                                    {isinsTotal} ISIN{isinsTotal === 1 ? "" : "s"} &rarr;
                                </MuiLink>
                            ) : undefined}
                        />
                    </Grid>
                </Section>

                <Section title="Addresses">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Legal Address</Typography>
                            <Typography variant="body2">{entityLegalAddressLines}</Typography>
                            <Typography variant="body2">{"ZIP " + entityLegalAddressPostalCode}</Typography>
                            <Typography variant="body2">{entityLegalAddressCity}</Typography>
                            <Typography variant="body2">{entityLegalAddressRegion}</Typography>
                            <Typography variant="body2">{countryLabel(entityLegalAddressCountry)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Headquarters Address</Typography>
                            <Typography variant="body2">{entityHeadquartersAddressLines}</Typography>
                            <Typography variant="body2">{"ZIP " + entityHeadquartersAddressPostalCode}</Typography>
                            <Typography variant="body2">{entityHeadquartersAddressCity}</Typography>
                            <Typography variant="body2">{entityHeadquartersAddressRegion}</Typography>
                            <Typography variant="body2">{countryLabel(entityHeadquartersAddressCountry)}</Typography>
                        </Grid>
                    </Grid>
                    {otherAddresses.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Other Addresses</Typography>
                            <Grid container spacing={3}>
                                {otherAddresses.map((address, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        {address.type && <Chip size="small" label={humanize(address.type)} sx={{ mb: 1 }} />}
                                        <Typography variant="body2">{(address.addressLines ?? []).join(", ")}</Typography>
                                        <Typography variant="body2">{[address.postalCode, address.city].filter(Boolean).join(" ")}</Typography>
                                        <Typography variant="body2">{address.region}</Typography>
                                        <Typography variant="body2">{countryLabel(address.country)}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Section>

                <Box sx={{ mb: 3 }}>
                    <AdSlot placement="lei_top" />
                </Box>

                <Section title="Registration Details">
                    <Grid container spacing={2}>
                        <InfoRow label="LEI First Issued" value={initialRegistrationDate} />
                        <InfoRow label="Entity Registration Date" value={entityCreationDateFull} />
                        <InfoRow label="Last LEI Update Date" value={entityLastUpdateFull} />
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block">Current LEI Status</Typography>
                            <Chip size="small" label={humanize(leiStatus)} color={leiStatusColor(leiStatus)} sx={{ mt: 0.5 }} />
                        </Grid>
                        <InfoRow label="Next LEI Renewal Date" value={leiNextRenewalDate} />
                        <InfoRow
                            label="LEI Issuer"
                            value={LeiIssuerCode ? (
                                <Stack spacing={0.25}>
                                    <span>{LeiIssuerName}</span>
                                    <MuiLink component={RouterLink} to={`/lei-details/${LeiIssuerCode}`} target="_blank" rel="noopener noreferrer">{LeiIssuerCode}</MuiLink>
                                </Stack>
                            ) : undefined}
                        />
                        <InfoRow
                            label="Managing LOU"
                            value={leiManagingLOU ? (
                                <Stack spacing={0.25}>
                                    <span>{LouName}</span>
                                    <MuiLink component={RouterLink} to={`/lei-details/${leiManagingLOU}`} target="_blank" rel="noopener noreferrer">{leiManagingLOU}</MuiLink>
                                </Stack>
                            ) : undefined}
                        />
                        <InfoRow label="Corroboration Level" value={humanize(leiCorroborationLevel)} />
                        <InfoRow
                            label="Data Validated At"
                            value={entityValidatedAtId ? (
                                <Stack spacing={0.25}>
                                    <span>
                                        {vaInternationalOrganizationName}
                                        {vaWebsite && <> &middot; <MuiLink href={vaWebsite} target="_blank" rel="noopener noreferrer">website</MuiLink></>}
                                    </span>
                                    <span>{vaCountry}</span>
                                    <span>{entityValidatedAtId}</span>
                                </Stack>
                            ) : undefined}
                        />
                        <InfoRow label="Data Validated As" value={entityValidatedAs} />
                        {otherValidationAuthorities.length > 0 && (
                            <InfoRow
                                label="Other Validation Authorities"
                                sm={12}
                                value={
                                    <Stack spacing={0.5}>
                                        {otherValidationAuthorities.map((authority, index) => (
                                            <Box key={index}>{authority.validatedAt?.id} &mdash; {authority.validatedAs}</Box>
                                        ))}
                                    </Stack>
                                }
                            />
                        )}
                    </Grid>
                </Section>

                <Section title="Parents">
                    <Grid container spacing={2}>
                        <InfoRow
                            label="Direct Parent"
                            sm={12}
                            value={DirectParentCode ? (
                                <MuiLink component={RouterLink} to={`/lei-details/${DirectParentCode}`} target="_blank" rel="noopener noreferrer">
                                    {DirectParentName} ({DirectParentCode})
                                </MuiLink>
                            ) : directParentException ? (
                                `No known parent - ${humanize(directParentException.reason)}`
                            ) : undefined}
                        />
                        <InfoRow
                            label="Ultimate Parent"
                            sm={12}
                            value={UltimateParentCode ? (
                                <MuiLink component={RouterLink} to={`/lei-details/${UltimateParentCode}`} target="_blank" rel="noopener noreferrer">
                                    {UltimateParentName} ({UltimateParentCode})
                                </MuiLink>
                            ) : ultimateParentException ? (
                                `No known parent - ${humanize(ultimateParentException.reason)}`
                            ) : undefined}
                        />
                    </Grid>
                </Section>

                <Section title="Children">
                    <Grid container spacing={2}>
                        <InfoRow
                            label={directChildrenTotal > 0 ? `Direct Children (${directChildrenTotal})` : "Direct Children"}
                            sm={12}
                            value={directChildren.length > 0 ? (
                                <Stack spacing={0.5}>
                                    {directChildren.map((child, index) => (
                                        <Box key={index}>
                                            <MuiLink component={RouterLink} to={`/lei-details/${child.lei}`} target="_blank" rel="noopener noreferrer">
                                                {child.legalName || "N/A"} ({child.lei})
                                            </MuiLink>
                                        </Box>
                                    ))}
                                    {directChildrenTotal > directChildren.length && (
                                        <MuiLink href={`https://search.gleif.org/#/record/${leiNumber}`} target="_blank" rel="noopener noreferrer">
                                            View all {directChildrenTotal} on GLEIF &rarr;
                                        </MuiLink>
                                    )}
                                </Stack>
                            ) : "No direct children available"}
                        />
                        <InfoRow
                            label={ultimateChildrenTotal > 0 ? `Ultimate Children (${ultimateChildrenTotal})` : "Ultimate Children"}
                            sm={12}
                            value={ultimateChildren.length > 0 ? (
                                <Stack spacing={0.5}>
                                    {ultimateChildren.map((child, index) => (
                                        <Box key={index}>
                                            <MuiLink component={RouterLink} to={`/lei-details/${child.lei}`} target="_blank" rel="noopener noreferrer">
                                                {child.legalName || "N/A"} ({child.lei})
                                            </MuiLink>
                                        </Box>
                                    ))}
                                    {ultimateChildrenTotal > ultimateChildren.length && (
                                        <MuiLink href={`https://search.gleif.org/#/record/${leiNumber}`} target="_blank" rel="noopener noreferrer">
                                            View all {ultimateChildrenTotal} on GLEIF &rarr;
                                        </MuiLink>
                                    )}
                                </Stack>
                            ) : "No ultimate children available"}
                        />
                    </Grid>
                </Section>

                {recentEvents.length > 0 && (
                    <Section title="Recent Changes">
                        <Stack spacing={1.5} divider={<Divider />}>
                            {recentEvents.map((event: any, index: number) => (
                                <Box key={index} sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                                    <Typography variant="body2" component="div" sx={{ display: "flex", alignItems: "center" }}>
                                        {humanize(event.type)}
                                        {event.status && <Chip size="small" label={humanize(event.status)} sx={{ ml: 1 }} />}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Effective {formatDateOnly(event.effectiveDate) ?? "N/A"} &middot; Recorded {formatDateOnly(event.recordedDate) ?? "N/A"}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Section>
                )}

                <Section title="External Links">
                    <Stack spacing={1}>
                        <Box>
                            <MuiLink href={`https://leiprofile.com/lei/${leiNumber}`} target="_blank" rel="noopener noreferrer" title={`Information for ${leiNumber} on LEIprofile Global Corporate Index`}>
                                LEIprofile: {leiNumber}
                            </MuiLink>
                        </Box>
                        <Box>
                            <MuiLink href={`https://search.gleif.org/#/record/${leiNumber}`} target="_blank" rel="noopener noreferrer" title={`Information for ${leiNumber} on GLEIF Website`}>
                                GLEIF: {leiNumber}
                            </MuiLink>
                        </Box>
                        {fieldModificationsLink && (
                            <Box>
                                <MuiLink href={fieldModificationsLink} target="_blank" rel="noopener noreferrer" title="Field-level change history (JSON)">
                                    Field Modification History (JSON)
                                </MuiLink>
                            </Box>
                        )}
                    </Stack>
                </Section>

                <Box sx={{ mb: 3 }}>
                    <AdSlot placement="lei_faq" />
                </Box>

                <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2 }}>FAQ</Typography>
                <Faq
                    entityLegalName={entityLegalName}
                    entityLegalAddressLines={entityLegalAddressLines}
                    entityLegalAddressCity={entityLegalAddressCity}
                    entityLegalAddressRegion={entityLegalAddressRegion}
                    entityLegalAddressCountry={entityLegalAddressCountry}
                    entityLegalAddressPostalCode={entityLegalAddressPostalCode}
                    entityHeadquartersAddressLines={entityHeadquartersAddressLines}
                    entityHeadquartersAddressCity={entityHeadquartersAddressCity}
                    entityHeadquartersAddressRegion={entityHeadquartersAddressRegion}
                    entityHeadquartersAddressCountry={entityHeadquartersAddressCountry}
                    entityHeadquartersAddressPostalCode={entityHeadquartersAddressPostalCode}
                    legalFormTransliteratedName={legalFormTransliteratedName ?? "N/A"}
                    entityCreationDate={entityCreationDate}
                    countries={countries}
                />

                <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button styleType="outlined" component={RouterLink} to="/search" startIcon={<ArrowBackIcon />} width="auto" noMarginTop>
                        Back to Search
                    </Button>
                    <Button styleType="outlined" component="a" href="https://leipapa.com/contact" title="Contact LEIpapa" endIcon={<EmailIcon />} width="auto" noMarginTop>
                        Contact Us
                    </Button>
                </Stack>

                <Box sx={{ my: 3 }}>
                    <AdSlot placement="lei_bottom" />
                </Box>

                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="body2" sx={{ textAlign: "justify" }}>
                        In today&rsquo;s fast-paced business world, understanding the dynamics and details of leading companies and counterparts is more crucial than ever. Whether you're a job seeker, investor, financial market participant, or curious consumer, getting accurate information about a company can significantly impact your decisions and strategies.
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", textAlign: "right", mt: 1 }}>
                        <MuiLink href="https://leipapa.com/authors/makhmud-efendi/" target="_blank" rel="noreferrer" title="Go to Makhmud's page at LEIpapa">M. Efendi</MuiLink>, founder of LEIpapa
                    </Typography>
                </Paper>
            </Box>
        </Container>
    )
}
